import { Checkbox, CheckboxProps, Spin, Tooltip, Typography } from "antd-v5";
import classNames from "classnames";
import InternalLink from "components/InternalLink";
import RiskScoreCellData from "components/RiskScoreCellData";
import { getVocabulary } from "components/Vocabulary";
import { useFindingBucket } from "contexts/FindingBucketContext";
import { useGlobalFilterContext } from "contexts/GlobalFilterContext";
import usePaginationControls from "hooks/usePaginationControls";
import { find, isEmpty, map, noop, reduce } from "lodash";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { CancellableAPIRequest, getEmptyCancellableRequest } from "restAPI";
import {
  FilterInfo,
  FilterValue,
  PaginationControls,
  SortChangeCallBack,
  SortInfo,
} from "types/pagination";
import { getStartGroupingCollapsed } from "utils/localStorageUtils";
import { formatNumber } from "utils/numberUtil";
import { SorterFunction } from "utils/sorters";
import { Sorters } from "utils/tableUtils";
import { getUrlSearch } from "utils/urlUtil";
import { CRA_DUE_DATE_KEYS } from "views/Findings/Constants";

import ACDropdown, { ACDropdownOptionProps } from "ac-components/ACDropdown";
import ACTable, {
  ACColumnMenuCommonProps,
  ACTableColumnType,
} from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import ChevronDownIcon from "ac-components/ac-icons/ChevronDownIcon";
import ChevronUpIcon from "ac-components/ac-icons/ChevronUpIcon";
import ColumnsIcon from "ac-components/ac-icons/ColumnsIcon";
import EllipsisVerticalIcon from "ac-components/ac-icons/EllipsisVerticalIcon";
import SortAscIcon from "ac-components/ac-icons/SortAscIcon";
import SortDescIcon from "ac-components/ac-icons/SortDescIcon";
import TableDrilldownIcon from "ac-components/ac-icons/TableDrilldownIcon";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";

import "./ACMultiGroupingTable.less";

type Props = {
  groupBy: string[];
  index: number;
  filterInfo?: FilterInfo;
  parentFieldName?: string;
  parentFieldValue?: FilterValue;
  sortInfo?: SortInfo;
  parentFilterId?: string;
  isParentGroupSelected?: boolean;
  showSecondaryCounts?: boolean;
  parentIndexSum?: number;
  otherGroupsVisible: Record<string, boolean> | undefined;
  setOtherGroupsVisible: React.Dispatch<
    React.SetStateAction<Record<string, boolean> | undefined>
  >;
  expandAll?: boolean;
  showRiskScoreColumn?: boolean;
  showDrillDownColumn?: boolean;
};

export type GroupStats = {
  name: string;
  displayName: string;
  riskScore: number;
  count: number;
  stats: Record<string, number>;
};

export default function ACMultiGroupingTable({
  groupBy,
  index,
  filterInfo,
  parentFieldName,
  parentFieldValue,
  sortInfo: parentSortInfo,
  parentFilterId = "root-group",
  isParentGroupSelected,
  parentIndexSum = 0,
  otherGroupsVisible,
  showSecondaryCounts = true,
  setOtherGroupsVisible,
  expandAll,
  showRiskScoreColumn = true,
  showDrillDownColumn = true,
}: Props) {
  if (isEmpty(groupBy)) {
    throw new Error("Group By list is empty");
  }
  const contextValue = useACMultiGroupingTable();
  const containerWidth = (contextValue.width || 0) - 40;
  const { globalFilterHash } = useGlobalFilterContext();
  const _groupByField = groupBy[index];
  const secondGroupByField = groupBy[index + 1];
  if (!_groupByField) {
    throw new Error("Group By Field is empty");
  }
  const {
    appliedFilters: actualAppliedFilters,
    fetchStats: fetchStatsAPI,
    fetchAllStats: fetchAllStatsAPI,
    columns: allColumns,
    groupByFieldRenderer,
    renderFinalItems,
    convertFilterField = defaultConvertFilterField,
    getFieldValue = defaultGetFieldValue,
    name,
    GroupDescriptionComponent,
    groupSelection = true,
    getCustomColumnTitles,
    disableSelectRootGroup = defaultDisableSelectRootGroup,
    countCellRenderer,
    countColumnOverride,
  } = contextValue;
  const [allStats, setAllStats] = useState<GroupStats[]>();
  const [stats, setStats] = useState<GroupStats[]>();
  const [loading, setLoading] = useState(true);
  const [groupByField, setGroupByField] = useState(_groupByField);
  const bucket = useFindingBucket();
  const { addFilter: addFilterToBucket } = bucket;

  const { startGroupsCollapsed } = useStartGroupingCollapsed();

  const [expandedStates, setExpandedStates] = useState<Record<number, boolean>>(
    {},
  );

  // Reset expansion when expandAll or startGroupsCollapsed changes
  useEffect(() => {
    if (expandAll === false) {
      setExpandedStates({});
    } else if (expandAll === undefined) {
      const initialState: Record<number, boolean> = {};
      if (!startGroupsCollapsed) {
        initialState[0] = true; // Only the first row is expanded
      }
      setExpandedStates(initialState);
    }
  }, [startGroupsCollapsed, expandAll]);

  // When expandAll becomes true and stats are available, expand all rows once
  useEffect(() => {
    if (expandAll === true && stats) {
      const allExpanded: Record<number, boolean> = {};
      stats.forEach((_, i) => {
        allExpanded[i] = true;
      });
      setExpandedStates(allExpanded);
    }
  }, [expandAll, stats?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setStats(undefined);
    setGroupByField(_groupByField);
  }, [_groupByField]);

  const groupByFilterInfo = useMemo(() => {
    const commonFilters = {
      ...actualAppliedFilters,
      ...filterInfo,
    };
    if (parentFieldName && parentFieldValue) {
      return {
        ...commonFilters,
        [convertFilterField(parentFieldName, parentFieldValue)]: getFieldValue(
          parentFieldName,
          parentFieldValue,
        ),
      };
    }
    return commonFilters;
  }, [
    filterInfo,
    parentFieldName,
    parentFieldValue,
    actualAppliedFilters,
    convertFilterField,
    getFieldValue,
  ]);

  const fetchAllStats = useCallback(() => {
    if (index !== 0) {
      return noop;
    }
    const { promise, cancel } = fetchAllStatsAPI({
      filters: groupByFilterInfo,
      groupByField: "",
      otherGroupByFields: [],
    });
    promise.then((response) => {
      setAllStats(response);
    });
    return cancel;
  }, [groupByFilterInfo, index, fetchAllStatsAPI]);

  const fetchStats = useCallback(() => {
    if (!groupByField) {
      return noop;
    }
    const cancel2 = fetchAllStats();
    setLoading(true);
    const { promise, cancel } = fetchStatsAPI({
      filters: groupByFilterInfo,
      groupByField,
      otherGroupByFields: groupBy.slice(index + 1) /* .length
        ? groupBy.slice(index + 1)
        : [groupBy[0]] */,
    });
    promise
      .then((response) => {
        setStats(response);
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      cancel();
      cancel2();
    };
  }, [
    groupByField,
    groupByFilterInfo,
    groupBy,
    index,
    fetchAllStats,
    // filterValues,
    fetchStatsAPI,
  ]);

  useEffect(() => {
    const cancel = fetchStats();
    const refresh = () => fetchStats();
    window.addEventListener("refreshList", refresh);
    return () => {
      cancel();
      window.removeEventListener("refreshList", refresh);
    };
  }, [fetchStats, globalFilterHash]);

  const tableControlls = usePaginationControls({
    defaultSortInfo: showRiskScoreColumn ? { riskScore: "descend" } : {},
  });
  const { sortInfo, onSortChange } = tableControlls;

  useEffect(() => {
    if (
      groupBy.includes("owaspTop10_2021") ||
      groupBy.includes("cweTop25_2024") ||
      !showRiskScoreColumn
    ) {
      onSortChange({});
    } else {
      onSortChange({
        riskScore: "descend",
      });
    }
  }, [groupBy, onSortChange, showRiskScoreColumn]);

  const isEntireGroupSelected = !!bucket.filterMap[parentFilterId];
  const columns = useMemo(() => {
    const selectEntireGrouping: CheckboxProps["onChange"] = (e) => {
      addFilterToBucket(
        parentFilterId,
        e.target.checked ? groupByFilterInfo : {},
        allStats?.[0].count ?? 0,
      );
    };
    const groupByFieldColumn = find(allColumns, {
      key: groupByField,
    });
    const secondGroupByFieldColumn = groupBy[1]
      ? find(allColumns, {
          key: groupBy[1],
        })
      : undefined;
    const thirdGroupByFieldColumn = groupBy[2]
      ? find(allColumns, {
          key: groupBy[2],
        })
      : undefined;
    const groupedFieldRender = groupByFieldRenderer
      ? groupByFieldRenderer(groupByField)
      : defaultGroupByRender();

    const column1Title = getColumnTitle(
      groupByFieldColumn,
      groupByField,
      getCustomColumnTitles,
    );
    const columns: ACTableColumnType<GroupStats>[] = [
      {
        title: "",
        key: "dummy1",
        width: 20,
      },
      {
        title: (
          <div
            style={
              groupSelection
                ? { paddingLeft: groupSelection ? 0 : 32 }
                : undefined
            }
          >
            {groupSelection && (
              <Checkbox
                style={{ marginRight: 16 }}
                onChange={selectEntireGrouping}
                disabled={
                  !allStats?.length || disableSelectRootGroup(groupByFilterInfo)
                }
                checked={isEntireGroupSelected}
              />
            )}
            {column1Title}
          </div>
        ),
        key: "grouped-field",
        dataIndex: ["displayName"],
        render: (_, stat) => {
          if (stat.name === "null") {
            return (
              <div className="text-muted">
                {"[No "}
                {column1Title}
                {"]"}
              </div>
            );
          }
          return <span title={stat.name}>{groupedFieldRender(stat)}</span>;
        },
        customMenuRender: (props) => (
          <CustomGroupByColumnMenu
            groupBy={groupBy}
            otherGroupsVisible={otherGroupsVisible}
            setOtherGroupsVisible={setOtherGroupsVisible}
            sortInfo={sortInfo}
            onSortChange={onSortChange}
            {...props}
          />
        ),
      },
    ];

    if (showSecondaryCounts && groupBy[1] && otherGroupsVisible?.group2Count) {
      columns.push({
        title: `${getColumnTitle(secondGroupByFieldColumn, groupBy[1], getCustomColumnTitles)} Count`,
        key: "group2Count",
        dataIndex: ["stats"],
        render: (_, record) => {
          return record.stats[groupBy[1]]
            ? formatNumber(record.stats[groupBy[1]], "long")
            : NOT_AVAILABLE;
        },
      });
    }
    if (showSecondaryCounts && groupBy[2] && otherGroupsVisible?.group3Count) {
      columns.push({
        title: `${getColumnTitle(thirdGroupByFieldColumn, groupBy[2], getCustomColumnTitles)} Count`,
        key: "group3Count",
        dataIndex: ["stats"],
        render: (_, record) => {
          return record.stats[groupBy[2]]
            ? formatNumber(record.stats[groupBy[2]], "long")
            : NOT_AVAILABLE;
        },
      });
    }

    columns.push(
      {
        title: `${name} Count`,
        dataIndex: ["count"],
        key: "count",
        render: (_, stat) => {
          return countCellRenderer
            ? countCellRenderer(groupByFilterInfo, {
                fieldName: groupByField,
                fieldValue: stat.name,
              })
            : formatNumber(stat.count || 0, "long");
        },
        sorter: Sorters.NUMBER("count"),
        width: `30%`,
        ...countColumnOverride,
      },
      ...(showRiskScoreColumn
        ? [
            {
              title: getVocabulary("riskScore"),
              dataIndex: ["riskScore"],
              key: "riskScore",
              sorter: Sorters.NUMBER("riskScore"),
              width: "30%",
              render: (_: unknown, stat: GroupStats) => {
                return (
                  <RiskScoreCellData
                    riskScore={stat.riskScore}
                    record={stat}
                    filters={groupByFilterInfo}
                    column={groupByField}
                    riskScoreType={undefined}
                  />
                );
              },
            },
          ]
        : []),
      {
        title: "",
        key: "dummy2",
        width: 20,
      },
    );
    const firstColumnWidthPercent =
      columns.length <= 4
        ? 0.7
        : columns.length === 5
          ? 0.4
          : columns.length === 6
            ? 0.35
            : 0.3;
    const findingCountWidthPercent = 0.3;
    const middleColumnCount = columns.length - 4;
    return columns.map((c, i) => ({
      ...c,
      width:
        i === 1
          ? containerWidth * firstColumnWidthPercent
          : i === columns.length - 3 && columns.length > 4
            ? containerWidth * findingCountWidthPercent
            : i > 0 && i < columns.length - 1 && middleColumnCount > 0
              ? (containerWidth *
                  (1 - (firstColumnWidthPercent + findingCountWidthPercent))) /
                middleColumnCount
              : c.width,
    })) as ACTableColumnType<GroupStats>[];
  }, [
    groupByField,
    allColumns,
    groupBy,
    containerWidth,
    otherGroupsVisible,
    onSortChange,
    sortInfo,
    setOtherGroupsVisible,
    groupByFieldRenderer,
    name,
    groupSelection,
    getCustomColumnTitles,
    addFilterToBucket,
    allStats,
    groupByFilterInfo,
    parentFilterId,
    isEntireGroupSelected,
    disableSelectRootGroup,
    countCellRenderer,
    countColumnOverride,
    // otherStats,
    showSecondaryCounts,
    showRiskScoreColumn,
  ]);

  const finalColumns = columns;

  const countColumns = useMemo(() => {
    const groupByFieldColumn = find(allColumns, {
      key: groupByField,
    });
    const secondGroupByFieldColumn = groupBy[1]
      ? find(allColumns, {
          key: groupBy[1],
        })
      : undefined;
    const thirdGroupByFieldColumn = groupBy[2]
      ? find(allColumns, {
          key: groupBy[2],
        })
      : undefined;

    const countColumns: ACTableColumnType<GroupStats>[] = [
      {
        title: "",
        key: "dummy1",
        width: 20,
      },
    ];

    if (showSecondaryCounts) {
      countColumns.push({
        title: "",
        key: "group1",
        dataIndex: [groupByField],
        render: (_, record) => {
          return record.stats[groupByField]
            ? `${(groupByField === "tags" || groupByField.startsWith("keyValue.")) && record.stats[groupByField] > 10000 ? "10,000+" : formatNumber(record.stats[groupByField], "long")} ${getColumnTitle(
                groupByFieldColumn,
                groupByField,
                getCustomColumnTitles,
              )}`
            : NOT_AVAILABLE;
        },
      });
    }

    if (showSecondaryCounts && groupBy[1] && otherGroupsVisible?.group2Count) {
      countColumns.push({
        title: "",
        key: "group2",
        dataIndex: [groupBy[1]],
        render: (_, record) => {
          return record.stats[groupBy[1]]
            ? `${formatNumber(record.stats[groupBy[1]], "long")} ${getColumnTitle(
                secondGroupByFieldColumn,
                groupBy[1],
                getCustomColumnTitles,
              )}`
            : NOT_AVAILABLE;
        },
      });
    }
    if (showSecondaryCounts && groupBy[2] && otherGroupsVisible?.group3Count) {
      countColumns.push({
        title: "",
        key: "group3",
        dataIndex: [groupBy[2]],
        render: (_, record) => {
          return record.stats[groupBy[2]]
            ? `${formatNumber(record.stats[groupBy[2]], "long")} ${getColumnTitle(
                thirdGroupByFieldColumn,
                groupBy[2],
                getCustomColumnTitles,
              )}`
            : "-";
        },
      });
    }

    countColumns.push(
      {
        title: "",
        dataIndex: ["count"],
        render: (_, record) => {
          // adding count may be invalid in case of overlapping groups
          const visibleFindingCount = Math.min(
            reduce(stats, (agg, stat) => agg + stat.count, 0),
            record.count,
          );
          const totalCountString = `${formatNumber(record.count, "long")} ${name}`;
          if (record.count !== visibleFindingCount) {
            return `${formatNumber(visibleFindingCount, "long")} out of ${totalCountString}`;
          }
          return totalCountString;
        },
      },
      ...(showRiskScoreColumn
        ? [
            {
              title: "",
              dataIndex: ["riskScore"],
              key: "riskScore",
              render: () => "",
            },
          ]
        : []),
      {
        title: "",
        key: "dummy2",
        width: 20,
      },
    );
    return countColumns.map((c, i) => ({
      ...c,
      width: finalColumns[i].width,
      onCell: () => ({ className: "text-muted" }),
    }));
  }, [
    groupByField,
    allColumns,
    groupBy,
    finalColumns,
    otherGroupsVisible,
    name,
    getCustomColumnTitles,
    stats,
    showSecondaryCounts,
    showRiskScoreColumn,
  ]);

  // TODO - create a common hook for local sorting
  const _sortInfo = parentSortInfo || sortInfo;
  const sortedStats = useMemo(() => {
    if (!isEmpty(_sortInfo)) {
      const sorters: { [key: string]: SorterFunction<GroupStats> } = {
        "grouped-field":
          groupByField === "severity"
            ? Sorters.SEVERITY("displayName", false)
            : Sorters.STRING("displayName"),
        group2Count: Sorters.NUMBER(`stats.${groupBy[1]}`),
        group3Count: Sorters.NUMBER(`stats.${groupBy[2]}`),
        riskScore: Sorters.NUMBER("riskScore"),
        count: Sorters.NUMBER("count"),
      };
      const sortedStats = stats ? [...stats] : [];
      sortedStats.sort((a, b) => {
        let comparison = 0;
        for (const i in _sortInfo) {
          if (sorters[i]) {
            const sortValue =
              sorters[i](a, b) * (_sortInfo[i] === "ascend" ? 1 : -1);
            comparison =
              comparison ||
              (i === "grouped-field"
                ? a.name === "null"
                  ? 1
                  : b.name === "null"
                    ? -1
                    : sortValue
                : sortValue);
          }
        }
        return comparison;
      });
      return sortedStats;
    }
    return stats;
  }, [_sortInfo, stats, groupByField, groupBy]);

  return (
    <div
      className={`ac-multi-grouping-table-by-wrapper group-by-level-${index}`}
    >
      {index === 0 ? (
        <ACTable<GroupStats>
          sortInfo={_sortInfo}
          pagination={false}
          onSortChange={onSortChange}
          className="ac-multi-grouping-table-by-table-header"
          columns={finalColumns}
          dataSource={[]}
          fullPage
          components={{
            body: {
              wrapper: () => <tr />,
            },
          }}
        />
      ) : null}
      <div className="ac-multi-grouping-table-list-wrapper">
        <Spin spinning={loading}>
          {isEmpty(sortedStats) ? (
            <div className="text-center p-y-l">
              <Typography.Text type="secondary">No Data</Typography.Text>
            </div>
          ) : (
            map(sortedStats, (stat, i) => {
              return (
                <GroupRow
                  columns={finalColumns}
                  row={stat}
                  rowIndex={i}
                  key={stat.name + "-" + i}
                  parentFilterId={parentFilterId}
                  filterInfo={groupByFilterInfo}
                  fieldName={groupByField}
                  fieldValue={stat.name!}
                  isParentGroupSelected={
                    !!isParentGroupSelected ||
                    !!bucket.filterMap[parentFilterId]
                  }
                  index={index}
                  showDrillDownColumn={showDrillDownColumn}
                  expanded={!!expandedStates[i]}
                  setExpanded={(expanded: boolean) =>
                    setExpandedStates((prev) => ({
                      ...prev,
                      [i]: expanded,
                    }))
                  }
                  expandedRender={(record, filterId) => {
                    const _isParentGroupSelected =
                      !!isParentGroupSelected ||
                      !!bucket.filterMap[filterId] ||
                      !!bucket.filterMap[parentFilterId];
                    return (
                      <>
                        {record.name && GroupDescriptionComponent ? (
                          <div className="ac-multi-grouping-table-group-description">
                            <GroupDescriptionComponent
                              groupByField={groupByField}
                              record={record}
                            />
                          </div>
                        ) : null}
                        {secondGroupByField ? (
                          <ACMultiGroupingTable
                            showSecondaryCounts={showSecondaryCounts}
                            groupBy={groupBy}
                            index={index + 1}
                            filterInfo={groupByFilterInfo}
                            parentFieldName={groupByField}
                            parentFieldValue={record.name}
                            parentFilterId={filterId}
                            sortInfo={_sortInfo}
                            isParentGroupSelected={_isParentGroupSelected}
                            parentIndexSum={parentIndexSum + i}
                            otherGroupsVisible={otherGroupsVisible}
                            setOtherGroupsVisible={setOtherGroupsVisible}
                            showRiskScoreColumn={showRiskScoreColumn}
                            showDrillDownColumn={showDrillDownColumn}
                            expandAll={expandAll}
                          />
                        ) : (
                          <div className="ac-multi-grouping-table-items-table">
                            {renderFinalItems({
                              filters: groupByFilterInfo,
                              parentFieldName: groupByField,
                              parentFieldValue: record.name!,
                              parentFilterId,
                              isParentGroupSelected: _isParentGroupSelected,
                            })}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              );
            })
          )}
        </Spin>
      </div>
      {index === 0 ? (
        <div style={{ position: "sticky", bottom: 0, zIndex: 10 }}>
          <ACTable<GroupStats>
            pagination={false}
            columns={countColumns}
            dataSource={allStats}
            fullPage
            showHeader={false}
            bordered
            locale={{ emptyText: <div /> }}
          />
        </div>
      ) : null}
    </div>
  );
}

function GroupRow({
  columns,
  row,
  rowIndex,
  expandedRender,
  parentFilterId,
  filterInfo,
  fieldName,
  fieldValue,
  isParentGroupSelected,
  index,
  showDrillDownColumn = true,
  expanded,
  setExpanded,
}: {
  columns: ACTableColumnType<GroupStats>[];
  row: GroupStats;
  rowIndex: number;
  expandedRender: (row: GroupStats, filterId: string) => React.ReactNode;
  parentFilterId: string;
  filterInfo: FilterInfo;
  fieldName: string;
  fieldValue: string;
  isParentGroupSelected: boolean;
  index: number;
  showDrillDownColumn?: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
}) {
  const { inView: inViewport, ref: inViewRef } = useInView();

  const { addFilter: addFilterToBucket, ...bucket } = useFindingBucket();
  const filterId = `${parentFilterId ? parentFilterId + ":" : ""}${fieldName}-${
    row.name
  }`;

  const {
    convertFilterField = defaultConvertFilterField,
    getFieldValue = defaultGetFieldValue,
    name,
    redirectTo,
    groupSelection = true,
  } = useACMultiGroupingTable();

  const currentFilterInfo = {
    ...filterInfo,
    [convertFilterField(fieldName, fieldValue)]: getFieldValue(
      fieldName,
      fieldValue,
    ),
  };
  const onCheckUnCheck: CheckboxProps["onChange"] = (e) => {
    addFilterToBucket(
      filterId,
      e.target.checked ? currentFilterInfo : {},
      row.count!,
    );
  };

  useEffect(() => {
    // if parent and child both are selected then unselect child
    if (parentFilterId && isParentGroupSelected && bucket.filterMap[filterId]) {
      addFilterToBucket(filterId, {}, 0);
    }
  }, [
    parentFilterId,
    bucket.filterMap,
    filterId,
    addFilterToBucket,
    isParentGroupSelected,
  ]);

  const filteredColumns = columns.filter((c) => c.title);
  // ENG-115797: currently we have null date in cra due date thatswhy we are disabling this.
  const isNullDate =
    CRA_DUE_DATE_KEYS.includes(fieldName) &&
    (fieldValue === null || fieldValue === "null");
  return (
    <>
      <div
        ref={inViewRef}
        className={classNames("ac-multi-grouping-table-row", {
          expanded,
          "has-selection": groupSelection,
        })}
        onClick={() => setExpanded(!expanded)}
      >
        {inViewport && (
          <>
            {groupSelection && (
              <div className="group-by-row-selection ac-multi-grouping-table-cell">
                <Checkbox
                  disabled={isParentGroupSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={onCheckUnCheck}
                  checked={
                    isParentGroupSelected || !!bucket.filterMap[filterId]
                  }
                />
              </div>
            )}
            {filteredColumns.map((c, i) => {
              return (
                <div
                  key={c.key}
                  className={classNames(
                    c.key as string,
                    "ac-multi-grouping-table-cell",
                  )}
                  style={{
                    width:
                      (c.width as number) +
                      (index === 0
                        ? 0
                        : i === filteredColumns.length - 1
                          ? index * -20
                          : 0),
                  }}
                >
                  {c.render!(row, row, rowIndex)}
                </div>
              );
            })}
            {showDrillDownColumn && (
              <div className="group-by-row-drilldown-button ac-multi-grouping-table-cell">
                <Tooltip title={`View in ${name}`}>
                  <InternalLink
                    to={`${redirectTo}?${getUrlSearch({
                      filters: currentFilterInfo,
                    })}`}
                  >
                    <ACButton
                      size="sm"
                      iconOnly
                      type="ghost"
                      onClick={(e) => e.stopPropagation()}
                      disabled={isNullDate}
                    >
                      <TableDrilldownIcon />
                    </ACButton>
                  </InternalLink>
                </Tooltip>
              </div>
            )}
            <div className="group-by-row-expand-icon ac-multi-grouping-table-cell">
              {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </div>
          </>
        )}
      </div>
      {expanded ? (
        <div className="ac-multi-grouping-table-expand-wrapper">
          {expandedRender(row, filterId)}
        </div>
      ) : null}
    </>
  );
}

export type RenderMethodType = (
  record: GroupStats,
) => React.ReactNode | React.ReactNode[];

const defaultGroupByRender = (): RenderMethodType => {
  return (record) => record.displayName;
};

export type RenderFinalItems = ({
  filters,
  parentFieldName,
  parentFieldValue,
  parentFilterId,
  isParentGroupSelected,
}: {
  filters: FilterInfo;
  parentFieldName: string;
  parentFieldValue: string;
  parentFilterId: string;
  isParentGroupSelected: boolean;
}) => ReactNode | ReactNode[];

export type FetchStats<T = GroupStats[]> = ({
  groupByField,
  otherGroupByFields,
  filters,
}: {
  groupByField: string;
  otherGroupByFields: string[];
  filters: FilterInfo;
}) => CancellableAPIRequest<T>;

export type CountCellRenderer = (
  filters: FilterInfo,
  { fieldName, fieldValue }: { fieldName: string; fieldValue: FilterValue },
) => ReactNode | ReactNode[];

export type ACMultiGroupingTableContextType = {
  width: number | undefined;
  appliedFilters: FilterInfo;
  columns: ACTableColumnType<any>[];
  fetchStats: FetchStats;
  fetchAllStats: FetchStats;
  groupByFieldRenderer?: (field: string) => RenderMethodType;
  renderFinalItems: RenderFinalItems;
  convertFilterField?: (fieldName: string, fieldValue?: FilterValue) => string;
  getFieldValue?: (fieldName: string, fieldValue: FilterValue) => FilterValue[];
  name: string;
  GroupDescriptionComponent?: React.ComponentType<{
    groupByField: string;
    record: GroupStats;
  }>;
  redirectTo: string;
  groupSelection?: boolean;
  getCustomColumnTitles?: () => Record<string, string>;
  disableSelectRootGroup?: (filterInfo: FilterInfo) => boolean;
  countCellRenderer?: CountCellRenderer;
  countColumnOverride?: Partial<ACTableColumnType<GroupStats>>;
};

export const ACMultiGroupingTableContext =
  React.createContext<ACMultiGroupingTableContextType>({
    width: 0,
    appliedFilters: {},
    fetchStats: () => getEmptyCancellableRequest<GroupStats[]>(),
    fetchAllStats: () => getEmptyCancellableRequest<GroupStats[]>(),
    columns: [],
    renderFinalItems: () => <></>,
    name: "",
    redirectTo: "",
    groupSelection: true,
  });
const useACMultiGroupingTable = () => useContext(ACMultiGroupingTableContext);

const CustomGroupByColumnMenu = ({
  groupBy = [],
  sortInfo,
  onSortChange,
  setOtherGroupsVisible,
  otherGroupsVisible,
  ...props
}: {
  groupBy: PaginationControls["groupBy"];
  otherGroupsVisible: Record<string, boolean> | undefined;
  setOtherGroupsVisible: React.Dispatch<
    React.SetStateAction<Record<string, boolean> | undefined>
  >;
  sortInfo: SortInfo;
  onSortChange: SortChangeCallBack;
} & ACColumnMenuCommonProps) => {
  const onOptionClick = (value: string | number) => {
    const _value = value as string;
    if (_value.startsWith("toggle")) {
      const columnKey = _value.replace("toggleG", "g");
      setOtherGroupsVisible((prev) => ({
        ...prev,
        [columnKey]: !prev?.[columnKey],
      }));
    } else {
      onSortChange({
        [_value]: sortInfo[_value] === "ascend" ? "descend" : "ascend",
      });
    }
  };

  const { columns: allColumns, getCustomColumnTitles } =
    useACMultiGroupingTable();
  const options: ACDropdownOptionProps[] = [];
  const groupByFieldColumn = find(allColumns, {
    key: groupBy[0],
  });
  const secondGroupByFieldColumn = groupBy[1]
    ? find(allColumns, {
        key: groupBy[1],
      })
    : undefined;
  const thirdGroupByFieldColumn = groupBy[2]
    ? find(allColumns, {
        key: groupBy[2],
      })
    : undefined;
  if (groupBy[0]) {
    options.push({
      label: `Sort by ${getColumnTitle(groupByFieldColumn, groupBy[0], getCustomColumnTitles)}`,
      value: "grouped-field",
      icon:
        sortInfo["grouped-field"] && sortInfo["grouped-field"] === "ascend" ? (
          <SortAscIcon />
        ) : (
          <SortDescIcon />
        ),
    });
  }
  if (groupBy[1]) {
    options.push({
      label: `Sort by ${getColumnTitle(
        secondGroupByFieldColumn,
        groupBy[1],
        getCustomColumnTitles,
      )} Count`,
      value: "group2Count",
      icon:
        sortInfo["group2Count"] && sortInfo["group2Count"] === "ascend" ? (
          <SortAscIcon />
        ) : (
          <SortDescIcon />
        ),
    });
  }
  if (groupBy[2]) {
    options.push({
      label: `Sort by ${getColumnTitle(
        thirdGroupByFieldColumn,
        groupBy[2],
        getCustomColumnTitles,
      )} Count`,
      value: "group3Count",
      icon:
        sortInfo["group3Count"] && sortInfo["group3Count"] === "ascend" ? (
          <SortAscIcon />
        ) : (
          <SortDescIcon />
        ),
    });
  }
  if (groupBy[1]) {
    options.push({
      type: "divider",
    });
    options.push({
      label: `${
        otherGroupsVisible?.["group2Count"] ? "Hide" : "Show"
      } ${getColumnTitle(secondGroupByFieldColumn, groupBy[1], getCustomColumnTitles)} Count`,
      value: "toggleGroup2Count",
      icon: <ColumnsIcon />,
    });
  }
  if (groupBy[2]) {
    options.push({
      label: `${
        otherGroupsVisible?.["group3Count"] ? "Hide" : "Show"
      } ${getColumnTitle(thirdGroupByFieldColumn, groupBy[2], getCustomColumnTitles)} Count`,
      value: "toggleGroup3Count",
      icon: <ColumnsIcon />,
    });
  }
  return (
    <ACDropdown
      items={options}
      placement="bottomRight"
      onOptionClick={onOptionClick}
      offsetY={10}
      onPopupVisibleChange={props.onPopupVisibleChange}
    >
      <ACButton
        size="sm"
        type="ghost"
        iconOnly
        className="ac-table-column-filter-sorter-group"
        onClick={(e) => e.stopPropagation()}
      >
        <EllipsisVerticalIcon />
      </ACButton>
    </ACDropdown>
  );
};

const getColumnTitle = (
  column: ACTableColumnType<any> | undefined,
  key: string,
  getCustomColumnTitles?: () => Record<string, string>,
) => {
  const customColumnTitles = getCustomColumnTitles
    ? getCustomColumnTitles()
    : {};
  return (
    column?.title ??
    customColumnTitles[key as keyof typeof customColumnTitles] ??
    key
  );
};

const defaultConvertFilterField = (field: string) => field;
const defaultGetFieldValue = (fieldName: string, fieldValue: FilterValue) => [
  fieldValue,
];

const defaultDisableSelectRootGroup = (filterInfo: FilterInfo) =>
  isEmpty(filterInfo);

type StartGroupingCollapsedContextType = {
  startGroupsCollapsed: boolean;
  setStartGroupsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

const StartGroupingCollapsedContext = createContext<
  StartGroupingCollapsedContextType | undefined
>(undefined);

export const StartGroupingCollapsedProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [startGroupsCollapsed, setStartGroupsCollapsed] = useState(() =>
    getStartGroupingCollapsed(),
  );

  return (
    <StartGroupingCollapsedContext.Provider
      value={{ startGroupsCollapsed, setStartGroupsCollapsed }}
    >
      {children}
    </StartGroupingCollapsedContext.Provider>
  );
};

export const useStartGroupingCollapsed =
  (): StartGroupingCollapsedContextType => {
    const context = useContext(StartGroupingCollapsedContext);
    if (!context) {
      throw new Error(
        "useStartGroupingCollapsed must be used within a StartGroupingCollapsedProvider",
      );
    }
    return context;
  };
